import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';

const usePaymentStatus = () => {
  const [paymentStatus, setPaymentStatus] = useState({
    hasValidPayment: false,
    paymentSession: null,
    isLoading: true,
    error: null
  });
  const { currentUser } = useAuth();

  // Check payment status
  const checkPaymentStatus = useCallback(async () => {
    if (!currentUser) {
      setPaymentStatus({
        hasValidPayment: false,
        paymentSession: null,
        isLoading: false,
        error: null
      });
      return;
    }

    try {
      setPaymentStatus(prev => ({ ...prev, isLoading: true, error: null }));

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${apiUrl}/api/check-payment-status`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'X-User-ID': currentUser.id
        }
      });

      if (!response.ok) {
        throw new Error(`Payment status check failed: ${response.status}`);
      }

      const data = await response.json();
      
      setPaymentStatus({
        hasValidPayment: data.hasValidPayment,
        paymentSession: data.paymentSession,
        isLoading: false,
        error: null
      });

      console.log('💳 Payment status checked:', {
        hasValidPayment: data.hasValidPayment,
        sessionId: data.paymentSession?.id
      });

      return data;
    } catch (error) {
      console.error('❌ Error checking payment status:', error);
      setPaymentStatus({
        hasValidPayment: false,
        paymentSession: null,
        isLoading: false,
        error: error.message
      });
      return null;
    }
  }, [currentUser]);

  // Check on mount and when user changes
  useEffect(() => {
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  // Refresh payment status (useful after payment completion)
  const refreshPaymentStatus = useCallback(() => {
    return checkPaymentStatus();
  }, [checkPaymentStatus]);

  // Check if user needs to pay
  const needsPayment = useCallback(() => {
    // Users with unlimited access don't need to pay
    if (currentUser?.user_metadata?.unlimited_access === true) {
      return false;
    }
    
    // Check if user has valid payment session
    return !paymentStatus.hasValidPayment;
  }, [currentUser, paymentStatus.hasValidPayment]);

  return {
    ...paymentStatus,
    checkPaymentStatus,
    refreshPaymentStatus,
    needsPayment
  };
};

export default usePaymentStatus;
